import {
  BehaviorConnection,
  BehaviorNode,
  EditorStyleActionKey,
  EventNode,
  getEditorStyleActionDefinition,
  PortValue,
} from "../index.js";
import {
  normalizeFontFamilyValue,
  normalizeAnimationDirectionValue,
  normalizeAnimationFillModeValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextDecorationLineValue,
  normalizeTextDecorationStyleValue,
  normalizeTextDecorationThicknessValue,
  normalizeTextTransformValue,
  normalizeWhiteSpaceValue,
  isFilterString,
  isBoxShadowString,
  isTextShadowString,
  resolveBindingExpression,
} from "../method.js";
import type {
  DataBinding,
  InjectedData,
  LayoutNodeStyle,
} from "../../layout/types.js";
import {
  buildLayoutListFilterScopedData,
  buildLayoutListSortScopedData,
} from "../../layout/resolution.js";
import {
  ACTION_SET_KEY_VALUE_TYPE,
  type ActionSetKey,
  type BindingExpression,
} from "../types.js";
import {
  BarLoaderTargetBindingPatch,
  ComponentBindingPatch,
  LocationBindingPatch,
  Loop,
  PatchResult,
  RowVariantBindingRule,
  TransformBindingPatch,
} from "./types.js";
import { ExecutionContext } from "./types.js";

export function makePortKey(
  nodeId: string,
  portId: string,
  loop?: Loop,
): string {
  return `${loop?.nodeId ?? "-"}:${loop?.index ?? "-"}:${nodeId}:${portId}`;
}

export function makeVisitKey(
  nodeId: string,
  portId: string,
  context: ExecutionContext,
): string {
  return `${context.id}:${nodeId}:${portId}`;
}

export function toFiniteNumber(raw: unknown, fallback = 0): number {
  const value = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

type BarLoaderTargetPatchLike = {
  target: { weight: number | "all"; part: string };
  key: string;
};

function barLoaderTargetPatchKey(patch: BarLoaderTargetPatchLike): string {
  return `${patch.target.weight}|${patch.target.part}|${patch.key}`;
}

function mergeBarLoaderTargetPatches(
  previous: BarLoaderTargetPatchLike[] | undefined,
  next: BarLoaderTargetPatchLike[] | undefined,
): BarLoaderTargetPatchLike[] | undefined {
  if (!previous?.length && !next?.length) return undefined;
  const byKey = new Map<string, BarLoaderTargetPatchLike>();
  for (const patch of previous ?? []) {
    byKey.set(barLoaderTargetPatchKey(patch), patch);
  }
  for (const patch of next ?? []) {
    byKey.set(barLoaderTargetPatchKey(patch), patch);
  }
  return byKey.size > 0 ? Array.from(byKey.values()) : undefined;
}

function barLoaderTargetBindingPatchKey(
  patch: BarLoaderTargetBindingPatch,
): string {
  return `${patch.target.weight}|${patch.target.part}|${patch.key}`;
}

function mergeBarLoaderTargetBindingPatches(
  previous: BarLoaderTargetBindingPatch[] | undefined,
  next: BarLoaderTargetBindingPatch[] | undefined,
): BarLoaderTargetBindingPatch[] | undefined {
  if (!previous?.length && !next?.length) return undefined;
  const byKey = new Map<string, BarLoaderTargetBindingPatch>();
  for (const patch of previous ?? []) {
    byKey.set(barLoaderTargetBindingPatchKey(patch), patch);
  }
  for (const patch of next ?? []) {
    byKey.set(barLoaderTargetBindingPatchKey(patch), patch);
  }
  return byKey.size > 0 ? Array.from(byKey.values()) : undefined;
}

function mergeRowVariantBindingRules(
  previous: RowVariantBindingRule[] | undefined,
  next: RowVariantBindingRule[] | undefined,
): RowVariantBindingRule[] | undefined {
  if (!previous?.length && !next?.length) return undefined;
  return [...(previous ?? []), ...(next ?? [])];
}

function mergeComponentBindingPatches(
  previous: ComponentBindingPatch | undefined,
  next: ComponentBindingPatch | undefined,
): ComponentBindingPatch | undefined {
  if (!previous && !next) return undefined;
  const merged: ComponentBindingPatch = { ...(previous ?? {}) };
  if (next?.listSource !== undefined) {
    merged.listSource = next.listSource;
  }
  if (next?.listFilterPredicate !== undefined) {
    merged.listFilterPredicate = next.listFilterPredicate;
  }
  if (next?.listSortComparator !== undefined) {
    merged.listSortComparator = next.listSortComparator;
  }
  if (next?.totalWeight !== undefined) {
    merged.totalWeight = next.totalWeight;
  }
  if (next?.barWeight !== undefined) {
    merged.barWeight = next.barWeight;
  }
  const mergedTargets = mergeBarLoaderTargetBindingPatches(
    previous?.barLoaderTargetBindingPatches,
    next?.barLoaderTargetBindingPatches,
  );
  if (mergedTargets?.length) {
    merged.barLoaderTargetBindingPatches = mergedTargets;
  } else {
    delete merged.barLoaderTargetBindingPatches;
  }
  const mergedRowVariants = mergeRowVariantBindingRules(
    previous?.rowVariantBindingRules,
    next?.rowVariantBindingRules,
  );
  if (mergedRowVariants?.length) {
    merged.rowVariantBindingRules = mergedRowVariants;
  } else {
    delete merged.rowVariantBindingRules;
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

export function emptyPatchResult(): PatchResult {
  return {
    stylePatch: {},
    textPatch: undefined,
    locationPatch: undefined,
    transformPatch: undefined,
    componentPatch: undefined,
    animation: undefined,
    bindingPatch: undefined,
    styleBindingPatch: undefined,
    locationBindingPatch: undefined,
    transformBindingPatch: undefined,
    componentBindingPatch: undefined,
  };
}

const BINDING_EXPRESSION_KINDS = new Set([
  "binding",
  "literal",
  "unary",
  "binary",
  "ternary",
]);

/**
 * Returns whether a value is a well-formed `BindingExpression` AST node.
 */
export function isBindingExpression(
  value: unknown,
): value is BindingExpression {
  if (!value || typeof value !== "object") return false;
  const kind = (value as { kind?: unknown }).kind;
  return typeof kind === "string" && BINDING_EXPRESSION_KINDS.has(kind);
}

/**
 * Normalizes a captured port value for `styleBindingPatch` / `bindingPatch`.
 * Runtime nodes may return a bare primitive (e.g. from `functionCallOutputs` or
 * `functionBegin` frames) when `binding` was not propagated; primitives are
 * wrapped as `{ kind: "literal" }` so render-time resolution does not drop them.
 */
export function ensureBindingExpression(
  value: unknown,
): BindingExpression | undefined {
  if (isBindingExpression(value)) {
    return value;
  }
  if (
    value === undefined ||
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return { kind: "literal", value };
  }
  return undefined;
}

/**
 * Reads a value port from a function/variable input frame.
 * When capturing bindings, preserves `BindingExpression` nodes from the frame.
 * When evaluating at runtime, resolves stored binding AST against `data`.
 */
export function resolveFramePortValue(
  value: unknown,
  data: unknown,
  captureBinding: boolean,
): unknown {
  if (captureBinding) {
    if (isBindingExpression(value)) {
      return value;
    }
    return ensureBindingExpression(value);
  }
  if (isBindingExpression(value)) {
    return resolveBindingExpression(value, data);
  }
  return value;
}

/** Normalizes a user/string port value to a supported CSS animation-direction keyword. */
export function coerceCssAnimationDirection(raw: unknown) {
  return normalizeAnimationDirectionValue(raw);
}

/** Normalizes a user/string port value to a supported CSS animation-fill-mode keyword. */
export function coerceCssAnimationFillMode(raw: unknown) {
  return normalizeAnimationFillModeValue(raw);
}

export function coerceToBoolean(raw: unknown): boolean {
  if (raw === true || raw === "true" || raw === 1) return true;
  if (raw === false || raw === "false" || raw === 0) return false;
  if (typeof raw === "string") return raw.length > 0;
  return raw != null;
}

export function stableSerialize(value: unknown): string {
  if (value === undefined) return "__undef__";
  if (typeof value === "number" && Number.isNaN(value)) return "__nan__";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function coerceActionStyleValue(
  key: EditorStyleActionKey,
  raw: PortValue | undefined,
): unknown {
  const def = getEditorStyleActionDefinition(key);
  if (!def) return raw;

  switch (def.inputKind) {
    case "boolean":
      return raw === true || raw === "true" || raw === 1;
    case "number": {
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isNaN(n) ? 0 : n;
    }
    case "color":
      return raw == null ? "#00000000" : String(raw);
    case "filter": {
      const candidate = String(raw ?? "").trim();
      return isFilterString(candidate) ? candidate : "none";
    }
    case "boxShadow": {
      const candidate = String(raw ?? "").trim();
      return isBoxShadowString(candidate) ? candidate : "none";
    }
    case "textShadow": {
      const candidate = String(raw ?? "").trim();
      return isTextShadowString(candidate) ? candidate : "none";
    }
    case "border": {
      const fallback = "1px solid #000000";
      const fromObject =
        raw && typeof raw === "object" && "border" in raw
          ? (raw as { border?: Record<string, unknown> }).border
          : undefined;
      if (fromObject) {
        return {
          border: {
            top:
              typeof fromObject.top === "string" && fromObject.top.trim()
                ? fromObject.top.trim()
                : fallback,
            right:
              typeof fromObject.right === "string" && fromObject.right.trim()
                ? fromObject.right.trim()
                : fallback,
            bottom:
              typeof fromObject.bottom === "string" && fromObject.bottom.trim()
                ? fromObject.bottom.trim()
                : fallback,
            left:
              typeof fromObject.left === "string" && fromObject.left.trim()
                ? fromObject.left.trim()
                : fallback,
          },
        };
      }
      const side = raw == null ? fallback : String(raw).trim() || fallback;
      return {
        border: {
          top: side,
          right: side,
          bottom: side,
          left: side,
        },
      };
    }
    case "borderRadius": {
      const fallback = "0px";
      const fromObject =
        raw && typeof raw === "object" && "radius" in raw
          ? (raw as { radius?: Record<string, unknown> }).radius
          : undefined;
      if (fromObject) {
        return {
          radius: {
            topLeft:
              typeof fromObject.topLeft === "string" &&
              fromObject.topLeft.trim()
                ? fromObject.topLeft.trim()
                : fallback,
            topRight:
              typeof fromObject.topRight === "string" &&
              fromObject.topRight.trim()
                ? fromObject.topRight.trim()
                : fallback,
            bottomRight:
              typeof fromObject.bottomRight === "string" &&
              fromObject.bottomRight.trim()
                ? fromObject.bottomRight.trim()
                : fallback,
            bottomLeft:
              typeof fromObject.bottomLeft === "string" &&
              fromObject.bottomLeft.trim()
                ? fromObject.bottomLeft.trim()
                : fallback,
          },
        };
      }
      const radius = raw == null ? fallback : String(raw).trim() || fallback;
      return {
        radius: {
          topLeft: radius,
          topRight: radius,
          bottomRight: radius,
          bottomLeft: radius,
        },
      };
    }
    case "spacing": {
      const asSideNumber = (value: unknown, fallback = 0): number => {
        const next = typeof value === "number" ? value : Number(value);
        return Number.isFinite(next) ? Math.max(0, Math.round(next)) : fallback;
      };
      const asScope = (
        value: unknown,
      ): "all" | "top" | "right" | "bottom" | "left" => {
        if (
          value === "all" ||
          value === "top" ||
          value === "right" ||
          value === "bottom" ||
          value === "left"
        ) {
          return value;
        }
        return "all";
      };
      const normalizeControl = (input: unknown) => {
        const raw =
          input && typeof input === "object"
            ? (input as Record<string, unknown>)
            : {};
        const scope = asScope(raw.scope);
        const value = asSideNumber(raw.value, 0);
        const sidesRaw =
          raw.sides && typeof raw.sides === "object"
            ? (raw.sides as Record<string, unknown>)
            : {};
        const sides = {
          top: asSideNumber(sidesRaw.top, value),
          right: asSideNumber(sidesRaw.right, value),
          bottom: asSideNumber(sidesRaw.bottom, value),
          left: asSideNumber(sidesRaw.left, value),
        };
        if (scope === "all") {
          sides.top = value;
          sides.right = value;
          sides.bottom = value;
          sides.left = value;
        } else {
          sides[scope] = value;
        }
        return { scope, value, sides };
      };
      const spacingRaw =
        raw && typeof raw === "object"
          ? (raw as unknown as Record<string, unknown>)
          : {};
      return {
        margin: normalizeControl(spacingRaw.margin),
        padding: normalizeControl(spacingRaw.padding),
      };
    }
    case "fontWeight":
      return normalizeFontWeightValue(raw);
    case "fontFamily":
      return normalizeFontFamilyValue(raw);
    case "textAlign":
      return normalizeTextAlignValue(raw);
    case "textTransform":
      return normalizeTextTransformValue(raw);
    case "whiteSpace":
      return normalizeWhiteSpaceValue(raw);
    case "textDecorationLine":
      return normalizeTextDecorationLineValue(raw);
    case "textDecorationStyle":
      return normalizeTextDecorationStyleValue(raw);
    case "textDecorationThickness":
      return normalizeTextDecorationThicknessValue(raw);
    case "string":
    default:
      return raw == null ? "" : String(raw);
  }
}

export function mergePatches(patches: PatchResult[]): PatchResult {
  const merged: PatchResult = {
    stylePatch: {},
    textPatch: undefined,
    locationPatch: undefined,
    transformPatch: undefined,
    componentPatch: undefined,
    animation: undefined,
    bindingPatch: undefined,
  };

  for (const patch of patches) {
    Object.assign(merged.stylePatch, patch.stylePatch);

    if (patch.textPatch !== undefined) {
      merged.textPatch = patch.textPatch;
    }

    if (patch.locationPatch) {
      merged.locationPatch = {
        ...(merged.locationPatch ?? {}),
        ...patch.locationPatch,
      };
    }

    if (patch.transformPatch) {
      merged.transformPatch = {
        ...(merged.transformPatch ?? {}),
        ...patch.transformPatch,
      };
    }

    if (patch.componentPatch) {
      const previousPatch = merged.componentPatch ?? {};
      const previousBarLoaderTargetPatches = (
        previousPatch as { barLoaderTargetPatches?: unknown[] }
      ).barLoaderTargetPatches;
      const nextBarLoaderTargetPatches = (
        patch.componentPatch as { barLoaderTargetPatches?: unknown[] }
      ).barLoaderTargetPatches;
      const previousRowVariantRules = (
        previousPatch as { rowVariantRules?: unknown[] }
      ).rowVariantRules;
      const nextRowVariantRules = (
        patch.componentPatch as { rowVariantRules?: unknown[] }
      ).rowVariantRules;
      merged.componentPatch = {
        ...previousPatch,
        ...patch.componentPatch,
      };
      const mergedArray = mergeBarLoaderTargetPatches(
        previousBarLoaderTargetPatches as
          | BarLoaderTargetPatchLike[]
          | undefined,
        nextBarLoaderTargetPatches as BarLoaderTargetPatchLike[] | undefined,
      );
      if (mergedArray && mergedArray.length > 0) {
        (
          merged.componentPatch as { barLoaderTargetPatches?: unknown[] }
        ).barLoaderTargetPatches = mergedArray;
      } else {
        delete (merged.componentPatch as { barLoaderTargetPatches?: unknown[] })
          .barLoaderTargetPatches;
      }
      const mergedRowVariantRules = [
        ...(previousRowVariantRules ?? []),
        ...(nextRowVariantRules ?? []),
      ];
      if (mergedRowVariantRules.length > 0) {
        (
          merged.componentPatch as { rowVariantRules?: unknown[] }
        ).rowVariantRules = mergedRowVariantRules;
      }
    }

    if (patch.animation !== undefined) {
      merged.animation = patch.animation;
    }

    if (patch.bindingPatch) {
      merged.bindingPatch = patch.bindingPatch;
    }

    if (
      patch.styleBindingPatch &&
      Object.keys(patch.styleBindingPatch).length > 0
    ) {
      merged.styleBindingPatch = {
        ...(merged.styleBindingPatch ?? {}),
        ...patch.styleBindingPatch,
      };
    }

    if (patch.locationBindingPatch) {
      merged.locationBindingPatch = {
        ...(merged.locationBindingPatch ?? {}),
        ...patch.locationBindingPatch,
      };
    }

    if (patch.transformBindingPatch) {
      merged.transformBindingPatch = {
        ...(merged.transformBindingPatch ?? {}),
        ...patch.transformBindingPatch,
      };
    }

    if (patch.componentBindingPatch) {
      merged.componentBindingPatch = mergeComponentBindingPatches(
        merged.componentBindingPatch,
        patch.componentBindingPatch,
      );
    }
  }

  return merged;
}

const DEFAULT_BORDER_SIDE = "1px solid #000000";

export const toBorderConfig = (value: unknown) => {
  const side = String(value ?? "").trim() || DEFAULT_BORDER_SIDE;
  return {
    border: {
      top: side,
      right: side,
      bottom: side,
      left: side,
    },
  };
};

const DEFAULT_BORDER_RADIUS = "0px";

export const toBorderRadiusConfig = (value: unknown) => {
  const radius = String(value ?? "").trim() || DEFAULT_BORDER_RADIUS;
  return {
    radius: {
      topLeft: radius,
      topRight: radius,
      bottomRight: radius,
      bottomLeft: radius,
    },
  };
};

/**
 * Coerces a runtime value to a plain object for `castToObject`.
 *
 * @param value - Raw input (object or JSON string).
 * @returns A non-array object; `{}` when parsing fails or the value is not an object.
 */
export const toObjectValue = (value: unknown): Record<string, unknown> => {
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed != null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
};

export const toListValue = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getNodePortValue = (
  node: BehaviorNode,
  portId: string,
): PortValue | undefined =>
  node.ports.find((port) => port.id === portId)?.value;

/**
 * Expands a coerced style value into its final CSS properties for the given key.
 *
 * Composite keys (`border`, `borderRadius`, `padding`) produce multiple CSS
 * properties. Simple keys (e.g. `color`, `backgroundColor`) produce a single entry.
 *
 * @param styleKey - The `EditorStyleActionKey` being set.
 * @param coercedRaw - The value returned by `coerceActionStyleValue` for the same key.
 * @returns A flat `Partial<LayoutNodeStyle>` ready to be merged into a style object.
 */
export function expandStyleKeyValue(
  styleKey: EditorStyleActionKey,
  coercedRaw: unknown,
): Partial<LayoutNodeStyle> {
  if (styleKey === "border") {
    const coerced = coercedRaw as {
      border?: { top: string; right: string; bottom: string; left: string };
    } | null;
    if (
      coerced &&
      typeof coerced === "object" &&
      "border" in coerced &&
      coerced.border
    ) {
      const { top, right, bottom, left } = coerced.border;
      const allSame = top === right && right === bottom && bottom === left;
      return {
        border: allSame ? top : undefined,
        borderTop: top,
        borderRight: right,
        borderBottom: bottom,
        borderLeft: left,
      };
    }
    return {};
  }

  if (styleKey === "borderRadius") {
    const coerced = coercedRaw as {
      radius?: {
        topLeft: string;
        topRight: string;
        bottomRight: string;
        bottomLeft: string;
      };
    } | null;
    if (
      coerced &&
      typeof coerced === "object" &&
      "radius" in coerced &&
      coerced.radius
    ) {
      const { topLeft, topRight, bottomRight, bottomLeft } = coerced.radius;
      const allSame =
        topLeft === topRight &&
        topRight === bottomRight &&
        bottomRight === bottomLeft;
      return {
        borderRadius: allSame ? topLeft : undefined,
        borderTopLeftRadius: topLeft,
        borderTopRightRadius: topRight,
        borderBottomRightRadius: bottomRight,
        borderBottomLeftRadius: bottomLeft,
      };
    }
    return {};
  }

  if (styleKey === "padding") {
    const coerced = coercedRaw as {
      padding?: {
        scope: string;
        value: number;
        sides?: { top: number; right: number; bottom: number; left: number };
      };
      margin?: {
        scope: string;
        value: number;
        sides?: { top: number; right: number; bottom: number; left: number };
      };
    } | null;
    if (
      coerced &&
      typeof coerced === "object" &&
      "margin" in coerced &&
      "padding" in coerced &&
      coerced.padding &&
      coerced.margin
    ) {
      const paddingSides = coerced.padding.sides ?? {
        top: coerced.padding.value,
        right: coerced.padding.value,
        bottom: coerced.padding.value,
        left: coerced.padding.value,
      };
      const marginSides = coerced.margin.sides ?? {
        top: coerced.margin.value,
        right: coerced.margin.value,
        bottom: coerced.margin.value,
        left: coerced.margin.value,
      };
      const allPaddingSame =
        paddingSides.top === paddingSides.right &&
        paddingSides.right === paddingSides.bottom &&
        paddingSides.bottom === paddingSides.left;
      const allMarginSame =
        marginSides.top === marginSides.right &&
        marginSides.right === marginSides.bottom &&
        marginSides.bottom === marginSides.left;
      return {
        padding: allPaddingSame ? `${paddingSides.top}px` : undefined,
        paddingTop: `${paddingSides.top}px`,
        paddingRight: `${paddingSides.right}px`,
        paddingBottom: `${paddingSides.bottom}px`,
        paddingLeft: `${paddingSides.left}px`,
        margin: allMarginSame ? `${marginSides.top}px` : undefined,
        marginTop: `${marginSides.top}px`,
        marginRight: `${marginSides.right}px`,
        marginBottom: `${marginSides.bottom}px`,
        marginLeft: `${marginSides.left}px`,
      };
    }
    return {};
  }

  return { [styleKey]: coercedRaw } as Partial<LayoutNodeStyle>;
}

/**
 * Re-evaluates style binding expressions with the current `InjectedData` and
 * returns a flat style object ready to merge, identical in shape to what
 * `stylePatch` used to contain.
 *
 * Called at render time so that styles depending on live data (e.g. lift
 * colours from `currentAthlete`) stay up-to-date without a full behavior drain.
 *
 * @param styleBindingPatch - Map of style keys to captured `BindingExpression`s.
 * @param data - Current `InjectedData` to evaluate against.
 * @returns Flat `Partial<LayoutNodeStyle>` with all style keys resolved.
 */
/**
 * Re-evaluates location binding expressions with the current `InjectedData`.
 *
 * @param locationBindingPatch - Captured per-axis expressions from `set(location)`.
 * @param data - Current injected data.
 * @returns Resolved partial `LocationValue`, or `undefined` when empty.
 */
function isPlainCompositeRecord(
  value: unknown,
): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Resolves per-axis composite binding patches (location/transform).
 *
 * When every field shares the same captured expression (typical for `ifElse` on
 * a whole composite port), the expression is evaluated once and fields are read
 * from the resulting object. Otherwise each field is resolved independently
 * (expanded `value.x` / `value.width` ports).
 */
function resolveCompositeFieldBindingPatch<T extends Record<string, number>>(
  patch: Partial<Record<keyof T, BindingExpression>> | undefined,
  fields: readonly (keyof T)[],
  data: unknown,
): Partial<T> | undefined {
  if (!patch) return undefined;

  const expressions = fields.map((field) =>
    ensureBindingExpression(patch[field]),
  );
  const defined = expressions.filter(
    (expression): expression is BindingExpression => Boolean(expression),
  );
  if (defined.length === 0) return undefined;

  const allFieldsShareWholeExpression =
    defined.length === fields.length &&
    defined.every((expression) => expression === defined[0]);

  if (allFieldsShareWholeExpression) {
    const whole = resolveBindingExpression(defined[0], data);
    if (isPlainCompositeRecord(whole)) {
      const result: Partial<T> = {};
      for (const field of fields) {
        if (Object.prototype.hasOwnProperty.call(whole, field)) {
          result[field] = toFiniteNumber(whole[String(field)]) as T[keyof T];
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }
  }

  const result: Partial<T> = {};
  for (const field of fields) {
    const expression = ensureBindingExpression(patch[field]);
    if (!expression) continue;
    const raw = resolveBindingExpression(expression, data);
    if (
      isPlainCompositeRecord(raw) &&
      Object.prototype.hasOwnProperty.call(raw, field)
    ) {
      result[field] = toFiniteNumber(raw[String(field)]) as T[keyof T];
      continue;
    }
    result[field] = toFiniteNumber(raw) as T[keyof T];
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function resolveLocationBindingPatch(
  locationBindingPatch: LocationBindingPatch | undefined,
  data: unknown,
): Partial<import("../types.js").LocationValue> | undefined {
  return resolveCompositeFieldBindingPatch(
    locationBindingPatch,
    ["x", "y", "z"],
    data,
  );
}

/**
 * Re-evaluates transform binding expressions with the current `InjectedData`.
 *
 * @param transformBindingPatch - Captured per-field expressions from `set(transform)`.
 * @param data - Current injected data.
 * @returns Resolved partial `TransformValue`, or `undefined` when empty.
 */
export function resolveTransformBindingPatch(
  transformBindingPatch: TransformBindingPatch | undefined,
  data: unknown,
): Partial<import("../types.js").TransformValue> | undefined {
  return resolveCompositeFieldBindingPatch(
    transformBindingPatch,
    ["width", "height", "rotation"],
    data,
  );
}

/**
 * Resolves component binding expressions into the legacy runtime `componentPatch`
 * shape (predicates, comparators, numeric props, bar-loader target patches).
 *
 * @param componentBindingPatch - Captured component bindings from behavior actions.
 * @param data - Base `InjectedData` for resolution.
 * @param listDataBinding - When set (behavior on a `layoutList`), scopes filter/sort
 *   predicates per row like the legacy runtime `getOutput` path.
 * @returns A plain object compatible with historical `componentPatch` consumers.
 */
export function resolveComponentBindingPatch(
  componentBindingPatch: ComponentBindingPatch | undefined,
  data: unknown,
  listDataBinding?: DataBinding,
): Record<string, unknown> | undefined {
  if (!componentBindingPatch) return undefined;
  const result: Record<string, unknown> = {};
  const baseData =
    data && typeof data === "object"
      ? (data as InjectedData)
      : ({} as InjectedData);

  const listSourceExpr =
    "listSource" in componentBindingPatch
      ? ensureBindingExpression(componentBindingPatch.listSource)
      : undefined;
  if (listSourceExpr) {
    result.listSource = toListValue(
      resolveBindingExpression(listSourceExpr, baseData),
    );
  }

  const filterExpr =
    "listFilterPredicate" in componentBindingPatch
      ? ensureBindingExpression(componentBindingPatch.listFilterPredicate)
      : undefined;
  if (filterExpr) {
    result.listFilterPredicate = (
      dataBinding: DataBinding,
      rowIndex: number,
      rowsOverride?: unknown[],
    ) => {
      const binding = listDataBinding ?? dataBinding;
      const scopedData = buildLayoutListFilterScopedData(
        baseData,
        binding,
        rowIndex,
        rowsOverride,
      );
      return coerceToBoolean(resolveBindingExpression(filterExpr, scopedData));
    };
  }

  const sortExpr =
    "listSortComparator" in componentBindingPatch
      ? ensureBindingExpression(componentBindingPatch.listSortComparator)
      : undefined;
  if (sortExpr) {
    result.listSortComparator = (
      dataBinding: DataBinding,
      aIndex: number,
      bIndex: number,
      latestInjectedData?: InjectedData,
      rowsOverride?: unknown[],
    ) => {
      const binding = listDataBinding ?? dataBinding;
      const base = (latestInjectedData ?? baseData) as InjectedData;
      const scopedData = buildLayoutListSortScopedData(
        base,
        binding,
        aIndex,
        bIndex,
        rowsOverride,
      );
      return toFiniteNumber(resolveBindingExpression(sortExpr, scopedData));
    };
  }

  if ("totalWeight" in componentBindingPatch) {
    const totalWeightExpr = ensureBindingExpression(
      componentBindingPatch.totalWeight,
    );
    if (totalWeightExpr) {
      result.totalWeight = toFiniteNumber(
        resolveBindingExpression(totalWeightExpr, data),
      );
    }
  }

  if ("barWeight" in componentBindingPatch) {
    const barWeightExpr = ensureBindingExpression(
      componentBindingPatch.barWeight,
    );
    if (barWeightExpr) {
      result.barWeight = toFiniteNumber(
        resolveBindingExpression(barWeightExpr, data),
      );
    }
  }

  const targetBindings = componentBindingPatch.barLoaderTargetBindingPatches;
  if (targetBindings?.length) {
    const resolvedTargets: Array<{
      target: BarLoaderTargetBindingPatch["target"];
      key: ActionSetKey;
      value: unknown;
    }> = [];
    for (const entry of targetBindings) {
      const expression = ensureBindingExpression(entry.expression);
      if (!expression) continue;
      const raw = resolveBindingExpression(expression, data);
      const value =
        entry.key === "text"
          ? raw == null
            ? ""
            : String(raw)
          : entry.key in ACTION_SET_KEY_VALUE_TYPE
            ? coerceActionStyleValue(
                entry.key as EditorStyleActionKey,
                raw as PortValue,
              )
            : raw;
      resolvedTargets.push({
        target: entry.target,
        key: entry.key,
        value,
      });
    }
    if (resolvedTargets.length > 0) {
      result.barLoaderTargetPatches = resolvedTargets;
    }
  }

  const rowVariantRules = resolveRowVariantBindingRules(
    componentBindingPatch.rowVariantBindingRules,
    baseData,
    listDataBinding,
  );
  if (rowVariantRules?.length) {
    result.rowVariantRules = rowVariantRules;
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Picks scoped {@link InjectedData} for a row-variant condition: uses the row
 * snapshot passed by `LayoutList` when present, otherwise rebuilds filter scope.
 */
function resolveRowVariantConditionScopedData(
  baseData: InjectedData,
  dataBinding: DataBinding | undefined,
  listDataBinding: DataBinding | undefined,
  injectedData: InjectedData,
): InjectedData {
  if (
    injectedData &&
    Object.prototype.hasOwnProperty.call(injectedData, "layoutListRowItem")
  ) {
    return injectedData;
  }
  const binding = listDataBinding ?? dataBinding;
  const rowIndex =
    typeof injectedData?.rowIndex === "number" &&
    Number.isFinite(injectedData.rowIndex)
      ? injectedData.rowIndex
      : 0;
  return buildLayoutListFilterScopedData(baseData, binding, rowIndex);
}

/**
 * Turns captured `createRowVariant` binding rules into runtime evaluators.
 *
 * @param rules - Rules captured at behavior drain time.
 * @param baseData - Base injected data for the owning `layoutList`.
 * @param listDataBinding - List binding on the `layoutList` node when known.
 * @returns Legacy `rowVariantRules` array for `LayoutList` consumers.
 */
function resolveRowVariantBindingRules(
  rules: RowVariantBindingRule[] | undefined,
  baseData: InjectedData,
  listDataBinding?: DataBinding,
):
  | Array<{
      name: string;
      order?: number;
      conditionEvaluator: (
        dataBinding: DataBinding,
        data: InjectedData,
      ) => boolean;
    }>
  | undefined {
  if (!rules?.length) return undefined;
  const resolved = rules
    .filter(
      (rule) => typeof rule.name === "string" && rule.name.trim().length > 0,
    )
    .map((rule, index) => {
      const expression = ensureBindingExpression(rule.conditionExpression);
      if (!expression) return null;
      const name = rule.name.trim();
      const order =
        typeof rule.order === "number" && Number.isFinite(rule.order)
          ? rule.order
          : index;
      return {
        name,
        order,
        conditionEvaluator: (
          dataBinding: DataBinding,
          injectedData: InjectedData,
        ) => {
          const scopedData = resolveRowVariantConditionScopedData(
            baseData,
            dataBinding,
            listDataBinding,
            injectedData,
          );
          return coerceToBoolean(
            resolveBindingExpression(expression, scopedData),
          );
        },
      };
    })
    .filter(
      (
        entry,
      ): entry is {
        name: string;
        order: number;
        conditionEvaluator: (
          dataBinding: DataBinding,
          data: InjectedData,
        ) => boolean;
      } => entry !== null,
    );
  return resolved.length > 0 ? resolved : undefined;
}

export function resolveStyleBindingPatch(
  styleBindingPatch:
    | Partial<Record<EditorStyleActionKey, BindingExpression>>
    | undefined,
  data: unknown,
): Partial<LayoutNodeStyle> {
  if (!styleBindingPatch) return {};
  const keys = Object.keys(styleBindingPatch) as EditorStyleActionKey[];
  if (keys.length === 0) return {};

  const result: Partial<LayoutNodeStyle> = {};
  for (const key of keys) {
    const expression = ensureBindingExpression(styleBindingPatch[key]);
    if (!expression) continue;
    const raw = resolveBindingExpression(expression, data);
    const coerced = coerceActionStyleValue(key, raw as PortValue);
    Object.assign(result, expandStyleKeyValue(key, coerced));
  }
  return result;
}

/**
 * Returns whether a behavior graph wires `onListExit` directly to `animPlay`.
 *
 * @param behavior - Behavior graph payload to inspect.
 * @returns True when at least one `onListExit` output connects to an `animPlay` action.
 */
export function hasOnListExitConnectedToAnimPlay(behavior?: {
  nodes?: BehaviorNode[];
  connections?: BehaviorConnection[];
}): boolean {
  const nodes = behavior?.nodes ?? [];
  const connections = behavior?.connections ?? [];
  if (nodes.length === 0 || connections.length === 0) return false;

  const exitEventNodes = nodes.filter(
    (node): node is EventNode =>
      node.type === "event" && node.kind === "onListExit",
  );
  if (exitEventNodes.length === 0) return false;

  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  for (const eventNode of exitEventNodes) {
    for (const connection of connections) {
      if (connection.fromNodeId !== eventNode.id) continue;
      const targetNode = nodeById.get(connection.toNodeId);
      if (targetNode?.type === "action" && targetNode.kind === "animPlay") {
        return true;
      }
    }
  }

  return false;
}

/**
 * Estimates how long a list-exit transition should wait before removing a row.
 *
 * The function looks for `onListExit` event nodes, follows their direct outgoing
 * connections, and inspects target `animPlay` action nodes.
 * - No `onListExit` event => `0`
 * - No outgoing `animPlay` from `onListExit` => `0`
 * - `animPlay` without a selected animation name => ignored
 * - Otherwise computes `delayMs + durationMs * iterationCount`
 *
 * When multiple valid `animPlay` nodes are found, the maximum wait time is returned.
 *
 * @param behavior - Behavior graph payload to inspect.
 * @returns Estimated wait time in milliseconds.
 */
export function estimateListExitWaitMsFromBehavior(behavior?: {
  nodes?: BehaviorNode[];
  connections?: BehaviorConnection[];
}): number {
  const nodes = behavior?.nodes ?? [];
  const connections = behavior?.connections ?? [];
  if (nodes.length === 0 || connections.length === 0) return 0;
  if (!hasOnListExitConnectedToAnimPlay(behavior)) return 0;

  const exitEventNodes = nodes.filter(
    (node): node is EventNode =>
      node.type === "event" && node.kind === "onListExit",
  );
  if (exitEventNodes.length === 0) return 0;

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  let maxWaitMs = 0;
  let hasValidAnimPlay = false;

  for (const eventNode of exitEventNodes) {
    const outgoingConnections = connections.filter(
      (connection) => connection.fromNodeId === eventNode.id,
    );
    if (outgoingConnections.length === 0) continue;

    for (const connection of outgoingConnections) {
      const targetNode = nodeById.get(connection.toNodeId);
      if (
        !targetNode ||
        targetNode.type !== "action" ||
        targetNode.kind !== "animPlay"
      ) {
        continue;
      }

      const animationName = String(
        getNodePortValue(targetNode, "name") ?? "",
      ).trim();
      if (!animationName) continue;

      if (
        targetNode.kind === "animPlay" &&
        Boolean((targetNode as { playInfinite?: boolean }).playInfinite)
      ) {
        maxWaitMs = Math.max(maxWaitMs, Number.MAX_SAFE_INTEGER);
        hasValidAnimPlay = true;
        continue;
      }

      const durationMs = Math.max(
        0,
        toFiniteNumber(getNodePortValue(targetNode, "durationMs"), 0),
      );
      const delayMs = Math.max(
        0,
        toFiniteNumber(getNodePortValue(targetNode, "delayMs"), 0),
      );
      const iterationCount = Math.max(
        1,
        Math.floor(
          toFiniteNumber(getNodePortValue(targetNode, "iterationCount"), 1),
        ),
      );

      const waitMs = delayMs + durationMs * iterationCount;
      maxWaitMs = Math.max(maxWaitMs, waitMs);
      hasValidAnimPlay = true;
    }
  }

  return hasValidAnimPlay ? maxWaitMs : 0;
}
